/**
 * The unsaved-changes guard (TMM-B5, FRM-008): while `dirty`, an in-app navigation that would
 * leave the page is held (react-router `useBlocker` — the app runs `createBrowserRouter`, a data
 * router — through the one `UnsavedGuardProvider` in the app shell) and the tab's own unload
 * shows the browser's "leave site?" prompt (`beforeunload`). The listener exists only while
 * dirty and is removed on clean, change and unmount. Outside a provider there is no in-app
 * blocking, only the unload prompt.
 *
 * What it deliberately does not hold:
 * - a page's own, programmatic query write (`useUrlState`'s `replace`, marked with
 *   `BACKGROUND_WRITE_STATE`): only Back / Forward and the moves a person makes are held, never a
 *   debounced search or a stale filter the page cleans up on load (TMMAPP-0082);
 * - a search-param-only change (a filter, a page, a tab — `useUrlState` replaces or pushes the
 *   query on the same path), unless the editor lives in the tab itself (`includeSearch`);
 * - the navigation a save triggers: while `busy` (the save is in flight) nothing is held, so the
 *   `navigate()` a successful submit makes is never prompted;
 * - `/auth/*` (sign-out, an expired session): those leave through a hard redirect
 *   (`window.location.assign`), where only the browser's own unload prompt applies, and it always
 *   offers Leave — a person is never trapped on a page they cannot save.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import { UnsavedGuardContext, type GuardSource } from './unsaved-guard-context';

export interface UnsavedGuardOptions {
  /** A save is in flight: navigation (a post-save redirect) is not held. */
  busy?: boolean;
  /** Also hold a change to `?search` on the same path (an editor that lives inside a tab). */
  includeSearch?: boolean;
  /**
   * An open dialog's close: called when another editor's question is about to show while this one
   * has nothing unsaved (`GuardSource.dismissClean`). Omitted (or `undefined`) for a page editor
   * and while the dialog is not open.
   */
  dismissWhenClean?: (() => void) | undefined;
}

export interface UnsavedGuard {
  /** A navigation is being held and the discard question should be shown. */
  blocked: boolean;
  /** "Discard changes": let the held navigation continue to its destination. */
  proceed: () => void;
  /** "Keep editing": drop the held navigation and stay. */
  stay: () => void;
}

export function useUnsavedGuard(dirty: boolean, options: UnsavedGuardOptions = {}): UnsavedGuard {
  const { busy = false, includeSearch = false, dismissWhenClean } = options;
  const registry = useContext(UnsavedGuardContext);
  const [asking, setAsking] = useState(false);
  const source = useRef<GuardSource>({ dirty, busy, includeSearch, ask: setAsking });

  // The provider reads the source at navigation time, so it is kept current after every commit.
  // A source that turns dirty moves to the top of the stack: behind a modal only the topmost
  // editor can be edited, so the most recently dirtied one is the one on screen.
  useEffect(() => {
    if (dirty && !source.current.dirty) registry?.touch(source.current);
    source.current.dirty = dirty;
    source.current.busy = busy;
    source.current.includeSearch = includeSearch;
    source.current.dismissClean = dismissWhenClean;
  }, [dirty, busy, includeSearch, dismissWhenClean, registry]);
  useEffect(() => registry?.register(source.current), [registry]);

  // One handler per editor: `addEventListener` de-dupes a shared function, so a shared one would be
  // removed for every dirty editor when the first of them turned clean.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers only show the prompt when `returnValue` is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return {
    blocked: asking && dirty,
    proceed: () => registry?.proceed(),
    stay: () => registry?.stay(),
  };
}
